import React, { FC, useEffect, useState } from 'react';
import { Box, Button, Skeleton } from '@ui';

import { FormProvider, useForm } from 'react-hook-form';

import useTranslation from '@app/hooks/use-translation';

// import OperatorsField from '@app/components/ui/easy-filter/blocks/operators-field';

import AppealFilterField from './appeal-filter-field';
import { AppealFilterItem, AppealFilterType, FilterOptionsListAndRules } from './types';

type EasyFilterProps = {
  data: AppealFilterType;
  // changeFilterStateHandle: React.Dispatch<React.SetStateAction<AppealFilterType & Range>>;
  onChange?: (data: AppealFilterType) => void;
  changeFilterOpenState: React.Dispatch<React.SetStateAction<boolean>>;
  keysForFiltering: FilterOptionsListAndRules[];
  isLoading?: boolean;
  disable?: boolean;
  modalMode?: boolean;
  dataIdStartDate?: string;
  dataIdEndDate?: string;
  additionalFilterKeys?: {
    keyName: string;
    options: FilterOptionsListAndRules[];
    buttonLabel: string;
    fieldName: 'id' | 'field';
  }[];
};

const AppealFilterModal: FC<EasyFilterProps> = (props) => {
  const {
    data: filterData,
    modalMode,
    disable,
    // changeFilterStateHandle,
    keysForFiltering,
    changeFilterOpenState,
    onChange,
    isLoading,
    additionalFilterKeys,
  } = props;
  // const textStyle = cn('font-[500]', 'text-[14px]', 'leading-[16px]', 'text-3color');
  const { t } = useTranslation('components.filter');
  const [data, changeFilterData] = useState(filterData);

  const methods = useForm<AppealFilterType>({ defaultValues: data });
  const { handleSubmit, getValues } = methods;

  // useEffect
  useEffect(() => {
    changeFilterData(filterData);
  }, [filterData]);

  const onSubmit = (value: AppealFilterType) => {
    const data = onFilterSendAppeal(value);
    onChangeFilter(data);
  };
  function onChangeFilter(data: AppealFilterType) {
    const allDataFormatting = Object.entries(data).reduce<Partial<AppealFilterType>>((acc, cur) => {
      const key = cur[0];
      const value = cur[1];
      acc[key] = value?.filter((_, index) => index !== value.length - 1);
      return acc;
    }, {});
    onChange?.(allDataFormatting as AppealFilterType);
    changeFilterOpenState(false);
  }
  const SkeletonLoading = (
    <div className={'pb-6'}>
      <div className={'flex w-full justify-around items-center pb-6'}>
        <Skeleton width={160} height={15} />
        <Skeleton width={160} height={15} />
        <Skeleton width={160} height={15} />
      </div>
      <div className={'flex flex-col justify-center w-full items-center gap-[15px]'}>
        <Skeleton width={987} height={66} />
        <Skeleton width={987} height={66} />
      </div>
    </div>
  );
  if (isLoading) return SkeletonLoading;

  return (
    <Box className="min-h relative">
      <div className={'w-full flex justify-center items-center '}>
        <FormProvider {...methods}>
          <div className={'relative w-full px-[70px]'}>
            <div
              className={
                'text-[12px] leading-[13px] flex pb-[13px] font-semibold uppercase items-center justify-around'
              }
            >
              <div>{t('alias')}</div>
              <div>{t('condition')}</div>
              <div>{t('values')}</div>
            </div>
            <div className="min-h-[300px]">
              <div className="flex flex-col gap-[32px]">
                <AppealFilterField keysForFiltering={keysForFiltering} />
                {additionalFilterKeys &&
                  additionalFilterKeys.map((additionalOption, index) => {
                    return (
                      <>
                        {getValues(additionalOption.keyName)?.length ? (
                          <AppealFilterField
                            key={additionalOption.keyName + index}
                            additionalObjectName={additionalOption.keyName}
                            keysForFiltering={additionalOption.options}
                            fieldName={additionalOption.fieldName}
                          />
                        ) : null}
                      </>
                    );
                  })}
                <div className="flex items-center justify-start gap-[5px]">
                  {additionalFilterKeys &&
                    additionalFilterKeys.map((additionalOption, index) => {
                      return (
                        <React.Fragment key={`button_${additionalOption.keyName + index}`}>
                          {getValues(additionalOption.keyName)?.length ? null : (
                            <Button
                              fill="outlined"
                              onClick={() => {
                                methods.setValue(additionalOption.keyName, [
                                  {
                                    [additionalOption.fieldName || 'field']: '',
                                    condition: '',
                                    value: [],
                                  } as unknown as AppealFilterItem,
                                ]);
                              }}
                              label={additionalOption.buttonLabel}
                            />
                          )}
                        </React.Fragment>
                      );
                    })}
                </div>
              </div>
            </div>
            <div className={'py-12 flex items-center justify-between'}>
              <div className={'flex items-center gap-[10px]'}>
                {modalMode ? (
                  <Button
                    fill={'filled'}
                    disabled={disable}
                    label={t('confirm')}
                    onClick={() => onSubmit(getValues())}
                  />
                ) : (
                  <Button fill={'filled'} label={t('confirm')} onClick={handleSubmit(onSubmit)} />
                )}
                <Button
                  fill={'linked'}
                  label={t('cancel')}
                  onClick={changeFilterOpenState.bind(null, false)}
                />
              </div>
            </div>
          </div>
        </FormProvider>
      </div>
      {/*modals*/}
    </Box>
  );
};

export default AppealFilterModal;

const onFilterSendAppeal = (data: AppealFilterType) => {
  const allDataFormatting = Object.entries(data).reduce<Partial<AppealFilterType>>((acc, cur) => {
    const key = cur[0];
    const value = cur[1];
    acc[key] = value?.map((filter) => {
      return {
        ...filter,
        value: Array.isArray(filter.value)
          ? filter.value.filter((item) => item).length === 1
            ? findComma(filter.value[0])
            : filter.value
                .filter((item) => item)
                .map((i) => findComma(i))
                .flat(1)
          : findComma(filter.value),
      };
    });
    return acc;
  }, {});
  return {
    ...data,
    ...allDataFormatting,
  };
};
function findComma(word: string) {
  const test = /,/g.test(word);
  if (test) {
    const result = word.split(',').map((word) => word.trim());
    return result;
  }
  return word;
}
